import { deepEqual, useTranslation } from '@gimlite/common';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import {
  FunctionComponent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useStoreUIV2 } from '../../hooks/useStore.hook';
import {
  WIZARD_CLOSE_ANIMATION_DELAY,
  WIZARD_REOPEN_DELAY,
} from '../../stores/wizard.store';
import { Button } from '../button/button.component';
import { Checkbox } from '../checkbox/checkbox.component';
import { Drawer, DrawerType } from '../drawer/drawer.component';
import { Empty } from '../empty/empty.component';
import { Status } from '../status/status.component';
import { Text } from '../text/text.component';
import './wizard.component.scss';

// Hook pour obtenir le store d'un wizard spécifique par son wizardInstanceId
export const useWizardStore = <T = any,>(
  wizardInstanceId?: string,
): T | null => {
  const { WizardStore } = useStoreUIV2();

  if (!wizardInstanceId) return null;

  const instance = WizardStore.getInstance(wizardInstanceId);
  return (instance?.store as T) || null;
};

// Hook pour obtenir l'instanceId du wizard actuel depuis les props du composant Wizard
export const useWizardInstanceId = (): string | null => {
  // Ce hook doit être appelé depuis un composant enfant de Wizard
  // Pour l'instant, on retourne null, mais on pourrait utiliser un contexte React
  return null;
};

export declare namespace WizardType {
  type Props<T> = {
    className?: string | null;
    defaultContext: T;
    title?: string | null;
    subtitle?: string | null;
    children:
      | React.ReactElement<WizardItemType.Props<T>>
      | React.ReactElement<WizardItemType.Props<T>>[];
    onClose?: () => void;
    isFinished?: boolean | null;
    finishedTitle?: string | null;
    finishedSubtitle?: string | null;
    finishedDescription?: string | null;
    enableButtonClose?: boolean | null;
    isLoading?: boolean | null;
    isError?: boolean | null;
    size?: DrawerType.Props['size'];
    wizardInstanceId?: string; // ID unique de cette instance de wizard
  };
}

export const Wizard = observer(
  <T,>({
    className,
    defaultContext,
    title,
    subtitle,
    children,
    onClose,
    isFinished = false,
    finishedTitle,
    finishedSubtitle,
    finishedDescription,
    enableButtonClose = true,
    isLoading: isLoadingProps,
    isError,
    size = 'large',
    wizardInstanceId,
  }: WizardType.Props<T>) => {
    const { WizardStore } = useStoreUIV2();
    const lastDefaultContextRef = useRef<T>(defaultContext);
    const [context, setContext] = useState<T>(defaultContext);
    console.log('🚀 ~ context:', context);
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [openAnotherAfterClose, setOpenAnotherAfterClose] = useState(false);
    const [isValidate, setIsValidate] = useState(false);

    const id = useId();
    const { t } = useTranslation();

    // Récupérer l'instance du wizard depuis le store
    const wizardInstance = wizardInstanceId
      ? WizardStore.getInstance(wizardInstanceId)
      : null;

    const childrenFormatted = useMemo(
      (): React.ReactElement[] =>
        (Array.isArray(children) ? children : [children])
          .filter(Boolean)
          .filter((element) => {
            if (element.props.if) {
              return element.props.if(context);
            }

            return true;
          }),
      [children, context],
    );

    const CurrentChild = useMemo(
      () => childrenFormatted.find((element, index) => index === currentStep),
      [currentStep, childrenFormatted],
    );

    const currentProps = useMemo(
      (): WizardItemType.Props<T> => ({
        enableFooter: true,
        enableNextButton: false,
        enablePreviousButton: true,
        enableDraftButton: false,
        enableCreateOtherButton: false,
        enablePrimaryButton: true,
        enableSecondaryButton: false,
        ...CurrentChild?.props,
      }),
      [CurrentChild, isFinished],
    );

    const CurrentComponent = useMemo(
      (): FunctionComponent<WizardItemType.Component<T>> =>
        currentProps?.component,
      [currentProps],
    );

    // Gérer la réouverture du wizard si "createAnother" est activé
    const shouldReopenWizard = useRef(false);

    useEffect(() => {
      shouldReopenWizard.current = openAnotherAfterClose;
    }, [openAnotherAfterClose]);

    /**
     * Rouvre le wizard avec un store vide après fermeture
     * Utilisé pour la fonctionnalité "Créer un autre"
     */
    const reopenWizardIfNeeded = (wizardId: string, baseStore?: any): void => {
      if (!shouldReopenWizard.current) return;

      const filteredStore = WizardStore.filterBaseStore(baseStore);

      // Attendre que le wizard soit complètement fermé avant de rouvrir
      setTimeout(() => {
        WizardStore.openNew({
          id: wizardId,
          store: filteredStore,
        });
        shouldReopenWizard.current = false;
      }, WIZARD_REOPEN_DELAY);
    };

    useEffect(() => {
      if (!deepEqual(lastDefaultContextRef.current, defaultContext)) {
        lastDefaultContextRef.current = defaultContext;
        setContext({ ...context, ...defaultContext });
      }
    }, [defaultContext]);

    useEffect(() => {
      if (isFinished && wizardInstanceId && wizardInstance) {
        // Attendre la fin de l'animation de succès avant de fermer
        setTimeout(() => {
          onClose?.();
          WizardStore.closeInstance(wizardInstanceId);

          // Rouvrir si "createAnother" est activé
          reopenWizardIfNeeded(wizardInstance.wizard.id, wizardInstance.store);
        }, WIZARD_CLOSE_ANIMATION_DELAY);
      }
    }, [isFinished, wizardInstanceId, wizardInstance, onClose]);

    return (
      <div className={classNames('wizard', className)}>
        <Drawer
          isLoading={isLoading || isLoadingProps}
          title={isFinished ? null : title}
          subTitle={isFinished ? null : subtitle}
          description={!isFinished ? CurrentChild?.props?.description : null}
          size={size}
          isOpen={true}
          enableButtonClose={enableButtonClose || isError}
          onClose={() => {
            onClose?.();
            setContext({ ...defaultContext } as T);
            setCurrentStep(0);
            setIsLoading(false);
            setOpenAnotherAfterClose(false);

            if (wizardInstanceId && wizardInstance) {
              WizardStore.closeInstance(wizardInstanceId);

              // Rouvrir si "createAnother" était activé
              reopenWizardIfNeeded(
                wizardInstance.wizard.id,
                wizardInstance.store,
              );
            }
          }}
          maskClosable={false}
        >
          <div
            className="wizard__content"
            style={{
              gridTemplateRows: currentProps?.enableFooter
                ? '1fr min-content'
                : '1fr',
            }}
          >
            <div className="wizard__content__body">
              {!isFinished ? (
                isError ? (
                  <div className="wizard__content__body__error">
                    <Empty variant="errorServer" />
                  </div>
                ) : (
                  CurrentComponent && (
                    <CurrentComponent
                      key={id}
                      next={() => {
                        setCurrentStep(currentStep + 1);
                      }}
                      previous={() => {
                        if (!(currentStep === 0)) {
                          setCurrentStep(currentStep - 1);
                        }
                      }}
                      loading={(value: boolean) => {
                        setIsLoading(value);
                      }}
                      context={context! ?? {}}
                      validate={(error: boolean) => {
                        setIsValidate(error);
                      }}
                      submit={(data) => {
                        setContext((currentContext) => ({
                          ...currentContext,
                          ...data,
                        }));
                      }}
                    />
                  )
                )
              ) : (
                <div className="wizard__content__body__finished">
                  <Status
                    icon="check"
                    backgroundColor="alert_success_20"
                    foregroundColor="alert_success"
                  />
                  <div className="wizard__content__body__finished__content">
                    <Text textAlign="center" variant="headline">
                      {finishedTitle || t('watermelon-confirmation')}
                    </Text>
                    <div className="wizard__content__body__finished__content__description">
                      {finishedSubtitle && (
                        <Text textAlign="center" variant="body">
                          {finishedSubtitle}
                        </Text>
                      )}
                      {finishedDescription && (
                        <Text textAlign="center" variant="button-m">
                          {finishedDescription}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {currentProps?.enableFooter && !isError && !isLoading && (
              <div
                className="wizard__content__footer"
                style={{
                  justifyContent: size === 'small' ? 'center' : 'space-between',
                }}
              >
                <div className="wizard__content__footer__item">
                  {currentProps?.enablePreviousButton && currentStep !== 0 && (
                    <Button
                      size="large"
                      iconLeft={'arrow_left'}
                      variant="outline"
                      onClick={() => {
                        setCurrentStep((currentStep) => currentStep - 1);
                        currentProps?.eventPreviousButton?.(context);
                      }}
                    >
                      {t('watermelon-previous')}
                    </Button>
                  )}
                  {currentProps?.enableCreateOtherButton && !isFinished && (
                    <Checkbox
                      onChange={(e) => {
                        setOpenAnotherAfterClose(!openAnotherAfterClose);
                        currentProps?.eventCreateOtherButton?.(
                          openAnotherAfterClose,
                        );
                      }}
                      size="xs"
                      label={t('watermelon-createAnother')}
                      checked={openAnotherAfterClose}
                    />
                  )}
                </div>
                <div className="wizard__content__footer__item">
                  {currentProps?.enableDraftButton && !isFinished && (
                    <Button
                      size="large"
                      variant="secondary"
                      onClick={() => {
                        currentProps?.eventDraftButton?.(context);
                      }}
                    >
                      {t('watermelon-saveDraft')}
                    </Button>
                  )}
                  {currentProps?.enableNextButton &&
                    currentStep !== childrenFormatted.length - 1 &&
                    !isFinished && (
                      <Button
                        size="large"
                        iconRight={'arrow_right'}
                        variant="primary"
                        onClick={() => {
                          setCurrentStep((currentStep) => currentStep + 1);
                          currentProps?.eventNextButton?.(context);
                        }}
                      >
                        {t('watermelon-next')}
                      </Button>
                    )}
                  {currentProps?.enableSecondaryButton && !isFinished && (
                    <Button
                      minWidth={true}
                      size="large"
                      variant="secondary"
                      onClick={() => {
                        currentProps?.eventSecondaryButton?.(context);
                      }}
                    >
                      {currentProps?.textSecondaryButton ||
                        t('watermelon-secondaryAction')}
                    </Button>
                  )}
                  {currentProps?.enablePrimaryButton &&
                    currentStep === childrenFormatted.length - 1 &&
                    !isFinished && (
                      <Button
                        minWidth={true}
                        size="large"
                        disabled={!isValidate}
                        variant="primary"
                        onClick={() => {
                          currentProps?.eventPrimaryButton?.(context);
                        }}
                      >
                        {currentProps?.textPrimaryButton ||
                          t('watermelon-primaryAction')}
                      </Button>
                    )}
                </div>
              </div>
            )}
          </div>
        </Drawer>
      </div>
    );
  },
);

export declare namespace WizardItemType {
  type Props<T> = {
    if?: (context: T) => boolean | undefined;
    description?: string | null;
    component: FunctionComponent<WizardItemType.Component<T>>;
    enableFooter?: boolean;
    enablePreviousButton?: boolean;
    enableNextButton?: boolean;
    enableDraftButton?: boolean;
    enableCreateOtherButton?: boolean;
    enablePrimaryButton?: boolean;
    enableSecondaryButton?: boolean;
    textPrimaryButton?: string | null;
    textSecondaryButton?: string | null;
    eventDraftButton?: (context: T) => void;
    eventPreviousButton?: (context: T) => void;
    eventCreateOtherButton?: (value: boolean) => void;
    eventNextButton?: (context: T) => void;
    eventPrimaryButton?: (context: T) => void;
    eventSecondaryButton?: (context: T) => void;
  };

  type Component<T> = {
    context: T;
    validate: (value: boolean) => void;
    submit: (value: Partial<T>) => void;
    loading: (value: boolean) => void;
    next: () => void;
    previous: () => void;
  };
}

export const WizardItem = <T,>({}: WizardItemType.Props<T>) => {
  return <></>;
};
